import React, { useState } from 'react';
import { User, Order, OrderStatus, UserRole } from '../types';
import { Button, StatusBadge } from './ui';
import { KanbanBoard } from './KanbanBoard';
import {
    LayoutList, Grip, Search, Plus, Calendar, DollarSign
} from 'lucide-react';

interface DentistOrdersProps {
    user: User;
    orders: Order[];
    onOrderClick: (order: Order) => void;
    onNewCase: () => void;
}

export const DentistOrders: React.FC<DentistOrdersProps> = ({
    user,
    orders,
    onOrderClick,
    onNewCase,
}) => {
    const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
    const [filter, setFilter] = useState('');

    // Filter orders for dentist
    const myOrders = orders.filter(o => o.doctorId === user.id || (user.role === UserRole.MODERATOR && o.doctorId === user.parentId));

    // Apply search filter
    const filteredOrders = myOrders.filter(o =>
        o.patientName.toLowerCase().includes(filter.toLowerCase()) ||
        o.id.includes(filter) ||
        (o.toothNumber && o.toothNumber.toString().includes(filter))
    );

    const getUrgencyColor = (order: Order) => {
        if (order.urgency === 'Urgent') return 'border-l-4 border-l-red-500';

        if (order.dueDate) {
            const days = Math.ceil((new Date(order.dueDate).getTime() - new Date().getTime()) / (86400000));
            if (days <= 2) return 'border-l-4 border-l-orange-500';
        }
        return 'border-l-4 border-l-transparent';
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 sticky top-0 md:top-20 z-20 bg-surface/80 backdrop-blur-md py-4 -my-4 px-1">
                <div className="flex items-center gap-2 bg-surface p-1 rounded-lg border border-border">
                    <button
                        onClick={() => setViewMode('list')}
                        className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-surfaceHighlight text-primary shadow-sm' : 'text-secondary hover:text-primary'}`}
                        title="List View"
                    >
                        <LayoutList size={18} />
                    </button>
                    <button
                        onClick={() => setViewMode('kanban')}
                        className={`p-2 rounded-md transition-all ${viewMode === 'kanban' ? 'bg-surfaceHighlight text-primary shadow-sm' : 'text-secondary hover:text-primary'}`}
                        title="Kanban Board"
                    >
                        <Grip size={18} />
                    </button>
                </div>

                <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                    <div className="relative flex-1 md:flex-none group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary group-focus-within:text-brand transition-colors" size={16} />
                        <input
                            className="bg-surface border border-border rounded-xl pl-10 pr-4 py-2.5 text-sm text-primary placeholder-secondary focus:border-brand focus:ring-1 focus:ring-brand outline-none w-full md:w-64 transition-all"
                            placeholder="Search patients, ID..."
                            value={filter}
                            onChange={e => setFilter(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={onNewCase}
                        className="bg-brand hover:brightness-110 text-white font-bold rounded-xl px-6 py-2.5 shadow-lg shadow-brand/20 transition-all flex items-center justify-center gap-2"
                    >
                        <Plus size={18} />
                        <span>New Case</span>
                    </button>
                </div>
            </div>

            {viewMode === 'kanban' ? (
                <KanbanBoard orders={filteredOrders} onStatusChange={() => { }} onOrderClick={onOrderClick} />
            ) : (
                <div className="bg-surface border border-border rounded-xl overflow-hidden shadow-card">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-surfaceHighlight text-secondary text-xs font-bold uppercase border-b border-border">
                                <tr>
                                    <th className="p-4 hidden md:table-cell">ID</th>
                                    <th className="p-4">Patient</th>
                                    <th className="p-4 hidden sm:table-cell">Type</th>
                                    <th className="p-4">Status</th>
                                    <th className="p-4 hidden lg:table-cell">Due Date</th>
                                    <th className="p-4 text-right">Price</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredOrders.map(order => (
                                    <tr
                                        key={order.id}
                                        onClick={() => onOrderClick(order)}
                                        className={`hover:bg-surfaceHighlight/50 cursor-pointer transition-colors group ${getUrgencyColor(order)}`}
                                    >
                                        <td className="p-4 font-mono text-secondary text-xs hidden md:table-cell">
                                            #{order.toothNumber || order.id.slice(0, 6)}
                                        </td>
                                        <td className="p-4">
                                            <div className="font-bold text-primary group-hover:text-brand transition-colors">{order.patientName}</div>
                                            <div className="sm:hidden text-xs text-secondary mt-1">{order.items[0]?.serviceName || 'Custom'}</div>
                                        </td>
                                        <td className="p-4 text-secondary hidden sm:table-cell">
                                            {order.items[0]?.serviceName || 'Custom'}
                                        </td>
                                        <td className="p-4">
                                            <StatusBadge status={order.status} />
                                            <div className="lg:hidden text-xs text-secondary mt-2 flex items-center gap-1">
                                                <Calendar size={10} />
                                                {order.dueDate ? new Date(order.dueDate).toLocaleDateString() : '-'}
                                            </div>
                                        </td>
                                        <td className="p-4 text-secondary hidden lg:table-cell font-mono text-xs">
                                            {order.dueDate ? new Date(order.dueDate).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="p-4 text-right group-hover:bg-surfaceHighlight/30">
                                            <div className="font-bold text-primary font-mono">{order.price}</div>
                                            <div className="text-[10px] text-secondary">EGP</div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredOrders.length === 0 && (
                                    <tr><td colSpan={6} className="p-12 text-center text-secondary">No orders found matching your search.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};
